import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RoomService, ToastMessageService } from '@fitness-force/api';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { CreateRoom, ListRoomResponse, Room } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ROOM_LIST_STATE } from '@fitness-force/state';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'select-room',
  templateUrl: './select-room.component.html',
  styleUrls: ['./select-room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    StyleClassModule,
    ErrorMsgComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class SelectRoomComponent implements OnInit, AfterViewInit {
  readonly addNewRoom = input<boolean>(false, { alias: 'visible' });
  readonly tenantId = input<number>(0);
  readonly roomId$ = input(new BehaviorSubject<number>(0), { alias: 'roomId' });
  readonly cssClass = input<string>('w-full text-sm');
  readonly formGroup = input<UntypedFormGroup>(new UntypedFormGroup({}));
  readonly controlName = input<UntypedFormControl>(new UntypedFormControl());
  readonly closeEvent = output<any>({ alias: 'closeEvent' });
  readonly selectedRoom = output<any>({ alias: 'selectedRoom' });

  CreateRoomForm: UntypedFormGroup = new UntypedFormGroup({});
  disableSaveBtn: boolean = false;
  roomList$ = new BehaviorSubject<any[]>([]);

  showAddNewRoom = signal(false);

  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly roomService = inject(RoomService);
  private readonly toastMessageService = inject(ToastMessageService);

  constructor() {
    effect(() => {
      this.showAddNewRoom.set(this.addNewRoom());
    });
  }

  ngOnInit(): void {
    this.GetRooms();
    this.CreateRoomForm = this.fb.group({
      name_primary_language: ['', [Validators.required]],
      name_secondary_language: [''],
    });
  }

  ngAfterViewInit(): void {
    const roomIdSubject = this.roomId$();
    if (roomIdSubject) {
      roomIdSubject.subscribe((res) => {
        this.setSelectedRoom(res);
      });
    }
  }

  GetRooms() {
    this.store.dispatch({ type: 'FETCH_ROOM_LIST' });
    this.store.select(ROOM_LIST_STATE).subscribe((responseData: Room[]) => {
      this.roomList$.next(responseData || []);
    });
  }

  reloadRoom(roomId: number) {
    this.roomService
      .GetRoomList(this.tenantId())
      .subscribe((responseData: ListRoomResponse) => {
        this.roomList$.next(responseData.items || []);
        const defaultSelectedRoom = this.roomList$.value.find(
          (o: Room) => o.id == roomId,
        );
        if (defaultSelectedRoom && defaultSelectedRoom.id > 0) {
          this.controlName().setValue(defaultSelectedRoom);
        }
      });
  }

  setSelectedRoom(roomId: number): void {
    if (roomId > 0) {
      const defaultSelectedRoom = this.roomList$.value.find(
        (o: Room) => o.id == roomId,
      );
      if (defaultSelectedRoom && defaultSelectedRoom.id > 0) {
        this.controlName().setValue(defaultSelectedRoom);
      }
    }
  }

  onRoomChange(event: any) {
    this.selectedRoom.emit(event.value);
  }

  clickAddNewRoom() {
    this.showAddNewRoom.set(true);
  }

  CreateRoom(form: any) {
    if (this.CreateRoomForm.valid) {
      this.disableSaveBtn = true;
      const createRoomRequest: CreateRoom = {} as CreateRoom;
      createRoomRequest.name_primary_language =
        form.value.name_primary_language;
      createRoomRequest.name_secondary_language =
        form.value.name_secondary_language;
      this.roomService
        .CreateRoom(this.tenantId(), createRoomRequest)
        .subscribe({
          next: async (response: any) => {
            this.disableSaveBtn = false;
            this.closeEvent.emit({ close: true });
            this.CreateRoomForm.reset();
            this.reloadRoom(response.id);
            this.closeEvent.emit({ closeRoomPopup: true, reloadData: true });
          },
          error: (error: any) => {
            this.disableSaveBtn = false;
            this.ShowToastErrors(error);
          },
        });
    } else {
      Object.keys(this.CreateRoomForm.controls).forEach((field) => {
        const control = this.CreateRoomForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  ShowToastErrors(error: any) {
    this.toastMessageService.showError(error);
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  closePopup() {
    this.showAddNewRoom.set(false);
    this.closeEvent.emit({ close: true });
    this.CreateRoomForm.reset();
    this.closeEvent.emit({ closeRoomPopup: true, reloadData: false });
  }

  get primaryNameFormControl(): UntypedFormControl {
    return this.CreateRoomForm.controls
      .name_primary_language as UntypedFormControl;
  }
}
